import React, { useRef } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { Calendar } from 'lucide-react';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Native <input type="date"> renders its segment order (MM/DD vs DD/MM vs
// YYYY-MM-DD) from the browser's own UI language, ignoring the page's lang
// attribute — so it can't be made to match the ÅÅÅÅ-MM-DD order used on the
// cards. Format as the user types instead, to keep the order fixed.
export function formatDateInput(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const year = digits.slice(0, 4);
    const month = digits.slice(4, 6);
    const day = digits.slice(6, 8);
    return [year, month, day].filter(Boolean).join('-');
}

export function isValidCalendarDate(value: string): boolean {
    if (!DATE_PATTERN.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

interface DueDateInputProps {
    /** YYYY-MM-DD, possibly partial while typing, or '' for none */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    autoFocus?: boolean;
}

/**
 * ÅÅÅÅ-MM-DD text field with a calendar button. The native date input is kept
 * invisible — its only job is to supply a browser calendar widget via
 * showPicker(); its value stays in sync with the visible text field so the
 * two never drift apart.
 */
const DueDateInput: React.FC<DueDateInputProps> = ({ value, onChange, disabled, autoFocus }) => {
    const nativeDateInputRef = useRef<HTMLInputElement>(null);

    const handleOpenPicker = () => {
        nativeDateInputRef.current?.showPicker?.();
    };

    return (
        <>
            <InputGroup>
                <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="ÅÅÅÅ-MM-DD"
                    value={value}
                    onChange={(e) => onChange(formatDateInput(e.target.value))}
                    disabled={disabled}
                    autoFocus={autoFocus}
                />
                <Button
                    variant="outline-secondary"
                    className="due-date-picker-btn"
                    onClick={handleOpenPicker}
                    disabled={disabled}
                    title="Välj datum"
                    style={{ borderColor: '#ced4da' }}
                >
                    <Calendar size={18} />
                </Button>
            </InputGroup>
            <input
                ref={nativeDateInputRef}
                type="date"
                value={isValidCalendarDate(value) ? value : ''}
                onChange={(e) => onChange(e.target.value)}
                tabIndex={-1}
                aria-hidden="true"
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            />
        </>
    );
};

export default DueDateInput;
